import { useEffect, useId, useRef, useState } from 'react';

import { PASSWORD_REVEAL_PADDING, PasswordReveal } from '@shared/ui/PasswordReveal';
import { Dialog, isCancelledSubmit } from '@shared/ui/dialog';

import { Field, FIELD_CONTROL_CLASS } from '../_shared/components';
import { sharedCopy } from '../_shared/copy';
import { copy } from './copy';
import { rotationSchema } from './forms.schema';

/**
 * Credential rotation. **The secret is not displayed, not echoed, and not
 * stored** — it is replaced.
 *
 * Three properties make that true rather than merely intended:
 *
 * - **The stored value is a mask, and the mask is a constant.** It is not a
 *   truncation of the real value and it is not fetched: the owning service
 *   returns `value: null` on a secret-classed key and reports only whether one
 *   is set. The field is read-only and out of the tab order, because it is a
 *   statement that a value exists rather than a control.
 * - **The new value never enters React state.** It is read from an uncontrolled
 *   input at the moment of the write, so no render holds it, no attribute
 *   carries it, and no error boundary can serialise it into a report.
 * - **The field is cleared on close whether the rotation committed or was
 *   cancelled.** A secret left in an element's `value` is readable from the
 *   browser's own tools after the dialog closed over it.
 *
 * The dialog says what rotation does to work already in flight, because the
 * operator's next question after pressing it is whether they just broke a
 * payment mid-flow.
 */
export function RotateDialog({
  open,
  busy,
  formError,
  onCommit,
  onCancel,
}: {
  open: boolean;
  busy: boolean;
  formError: string | null;
  /** Receives the pasted value once and never sees it again. */
  onCommit: (value: string) => void;
  onCancel: () => void;
}) {
  const headingId = useId();
  const secretRef = useRef<HTMLInputElement>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  /** One clear, on every close and on every open. */
  function clearSecret() {
    if (secretRef.current) secretRef.current.value = '';
    setFieldError(null);
  }

  useEffect(() => {
    if (open) clearSecret();
  }, [open]);

  return (
    <Dialog
      open={open}
      labelledBy={headingId}
      onClose={(returnValue) => {
        clearSecret();
        if (returnValue !== 'rotate') onCancel();
      }}
    >
      <form
        method="dialog"
        onSubmit={(event) => {
          // «ألغِ» is a submit too — see `isCancelledSubmit`. Left alone, it lets
          // `method="dialog"` close the element, so an operator who opened the
          // rotation and thought better of it does not rotate the secret on the
          // way out. The empty-field guard below never ran on that path: the
          // exit carries `formNoValidate` and the field is untouched, so the
          // dialog closed and the write went with it.
          if (isCancelledSubmit(event, 'rotate')) return;
          event.preventDefault();

          const parsed = rotationSchema.safeParse(secretRef.current?.value ?? '');
          if (!parsed.success) {
            setFieldError(parsed.error.issues[0]?.message ?? copy.rotate.newRequired);
            return;
          }

          setFieldError(null);
          onCommit(parsed.data);
          // The caller closes the dialog on success; the value leaves the
          // element either way, in the same tick it was read.
          clearSecret();
        }}
        className="flex flex-col gap-4"
      >
        <div>
          <h2 id={headingId} className="text-h3">
            {copy.rotate.title}
          </h2>
          <p className="mb-0 mt-1 max-w-none text-xs text-ink-muted">{copy.rotate.lede}</p>
        </div>

        {formError ? (
          <p role="alert" className="mb-0 max-w-none text-sm text-danger">
            {formError}
          </p>
        ) : null}

        <Field label={copy.rotate.storedLabel} hint={copy.rotate.storedHint}>
          {(id, describedBy) => (
            <input
              id={id}
              type="text"
              dir="ltr"
              value={copy.rotate.storedMask}
              readOnly
              aria-readonly
              tabIndex={-1}
              aria-describedby={describedBy}
              className={`mono ${FIELD_CONTROL_CLASS} text-ink-muted`}
            />
          )}
        </Field>

        <Field label={copy.rotate.newLabel} hint={copy.rotate.newHint} error={fieldError ?? undefined}>
          {(id, describedBy, invalid) => (
            <PasswordReveal>
              {(type) => (
                <input
                  ref={secretRef}
                  id={id}
                  type={type}
                  dir="ltr"
                  autoComplete="off"
                  spellCheck={false}
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  aria-required
                  required
                  className={`mono ${FIELD_CONTROL_CLASS} ${PASSWORD_REVEAL_PADDING}`}
                />
              )}
            </PasswordReveal>
          )}
        </Field>

        <div className="flex flex-wrap gap-2">
          {/*
           * The field above is `required`, so without `formNoValidate` the
           * browser would block the **exit** and demand a secret from an
           * operator who has just decided not to rotate one.
           */}
          <button
            type="submit"
            value="cancel"
            formNoValidate
            onClick={onCancel}
            className="rounded-sm border border-border-strong px-5 py-3 text-sm text-ink hover:bg-hover-bg"
          >
            {sharedCopy.action.cancel}
          </button>

          <button
            type="submit"
            value="rotate"
            disabled={busy}
            className="rounded-sm border border-warning px-5 py-3 text-sm text-warning hover:bg-warning-soft disabled:cursor-not-allowed disabled:opacity-45"
          >
            {copy.rotate.commit}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
